import type { ISelectOption } from '../../ui/base/select/select';
import type { FilterMethod, ICustomFilter, IFilterSchema } from '../../ui/data-table/components/filters/filters.models';
import { addDays, IDateOnlyRange } from './claim-responsibility-range.util';
import { ResponsibilityType } from './claim-responsibility-report.util';

/**
 * How the "Active Claims By Responsibility" widget and the claims table filter each other (FE-151),
 * on both consoles: the provider Claims page and the Support claims list.
 *
 * The selection lives in BOTH places — the widget's store and the table's filters — and these
 * functions translate between them, so the page can mirror one into the other without either
 * owning the truth. The table's filters are what gets saved and restored, so on load the table wins.
 *
 * Measured on sandbox `GET /provider/claim`, 2026-10-06:
 * - `filterCreated=BT:<start>T00:00:00.000Z:<end+1>T00:00:00.000Z` with a responsibility type, or
 *   type + category, returned EXACTLY the report's count for every type and category, on two windows;
 * - two separate Created filters (`GTE:start` + `LT:end`) are NOT ANDed: they returned all 991
 *   claims. So the range must travel as one BT value, and must not be added beside a Created filter
 *   the user set themselves.
 *
 * Type-only imports from the rest of core, never the barrel, so it runs under the DOM-free jest config.
 */

export const RESPONSIBILITY_FILTER_ID = 'filterClaimResponsibilityType';
export const CATEGORY_FILTER_ID = 'filterClaimResponsibilityCategoryType';
export const CREATED_FILTER_ID = 'filterCreated';

const EQ = 'EQ' as FilterMethod;
const BT = 'BT' as FilterMethod;
const RESPONSIBILITY_TYPES: ReadonlyArray<ResponsibilityType> = ['ENTER', 'PAYER', 'PROVIDER', 'GUARANTOR', 'NONE', 'UNKNOWN'];

export interface IResponsibilitySelection {
    type?: ResponsibilityType;
    /** Only meaningful with `type`. */
    category?: string;
}

/**
 * The single value an "equals" Select filter holds for `filterId`, or `undefined` when there is no
 * such filter or it says something the widget cannot show as one selection: several filters on the
 * field, a multi-value select, or a method other than EQ ("is not Payer" selects no chip).
 */
function singleEqValue(filters: ICustomFilter[], filterId: string): string | undefined {
    const matching = filters.filter(f => f.filterId === filterId);
    if (matching.length !== 1) {
        return undefined;
    }
    const [filter] = matching;
    if (filter.filterType !== 'Select' || filter.filterMethod !== EQ || !filter.value || Array.isArray(filter.value)) {
        return undefined;
    }
    const value = (filter.value as ISelectOption).value;
    return typeof value === 'string' && value ? value : undefined;
}

/** What the widget should show selected, given the table's applied filters. */
export function selectionFromFilters(filters: ICustomFilter[]): IResponsibilitySelection {
    const raw = singleEqValue(filters, RESPONSIBILITY_FILTER_ID);
    const type = RESPONSIBILITY_TYPES.includes(raw as ResponsibilityType) ? (raw as ResponsibilityType) : undefined;
    if (!type) {
        return {};
    }
    const category = singleEqValue(filters, CATEGORY_FILTER_ID);
    return category ? { type, category } : { type };
}

export function isSameSelection(a: IResponsibilitySelection, b: IResponsibilitySelection): boolean {
    return (a.type ?? undefined) === (b.type ?? undefined) && (a.category ?? undefined) === (b.category ?? undefined);
}

/**
 * A Select filter built from the schema: its option, so the value reads exactly as if it had been
 * picked in the panel, and its label, so the chip says "Responsibility = PAYER" (the design's chip)
 * rather than the `startCase(filterId)` fallback, "Filter Claim Responsibility Type".
 */
function selectFilter(schemas: IFilterSchema[], filterId: string, value: string): ICustomFilter {
    const schema = schemas.find(s => s.filterId === filterId) as { label?: string; options?: ISelectOption[] } | undefined;
    const option = schema?.options?.find(o => o.value === value) ?? { label: value, value };
    return { filterId, filterType: 'Select', filterMethod: EQ, value: option, ...(schema?.label ? { label: schema.label } : {}) } as ICustomFilter;
}

/**
 * The table's filters with the responsibility and category fields replaced by `selection` — every
 * other filter kept as the same object. One array, so the table re-fetches once, not per field.
 */
export function filtersWithSelection(filters: ICustomFilter[], selection: IResponsibilitySelection, schemas: IFilterSchema[]): ICustomFilter[] {
    const others = filters.filter(f => f.filterId !== RESPONSIBILITY_FILTER_ID && f.filterId !== CATEGORY_FILTER_ID);
    if (!selection.type) {
        return others;
    }
    const next = [...others, selectFilter(schemas, RESPONSIBILITY_FILTER_ID, selection.type)];
    if (selection.category) {
        next.push(selectFilter(schemas, CATEGORY_FILTER_ID, selection.category));
    }
    return next;
}

export type CreatedScope =
    /** No responsibility selected: the table is not limited by the widget. */
    | { kind: 'none' }
    /** Limited to the widget's range — the table's counts match the widget's. */
    | { kind: 'applied'; filter: ICustomFilter }
    /** A responsibility is selected but the user has their own Created filter, which wins. */
    | { kind: 'overridden' };

/**
 * Whether the claims request should be limited to the widget's create-date range.
 *
 * Only while a responsibility is selected — that is when the table is meant to show the widget's
 * slice. Never beside a Created filter the user set: the API does not AND two of them, it widens to
 * every claim, so adding ours would silently undo theirs.
 */
export function createdScope(filters: ICustomFilter[], range: IDateOnlyRange): CreatedScope {
    if (!selectionFromFilters(filters).type) {
        return { kind: 'none' };
    }
    if (filters.some(f => f.filterId === CREATED_FILTER_ID)) {
        return { kind: 'overridden' };
    }
    return {
        kind: 'applied',
        // A String filter so the value is sent verbatim: `BT:<start>T00:00:00.000Z:<end+1>T00:00:00.000Z`,
        // the same half-open UTC-day window the report used. The DateTime filter type cannot carry a range.
        filter: {
            filterId: CREATED_FILTER_ID,
            filterType: 'String',
            filterMethod: BT,
            value: `${range.start}T00:00:00.000Z:${addDays(range.end, 1)}T00:00:00.000Z`,
        } as ICustomFilter,
    };
}
